import * as React from "react";
import DialogTitle from "@mui/material/DialogTitle";
import Dialog from "@mui/material/Dialog";
import { useMutation, useQuery } from "react-query";
import { ApplicationSummary, ClientConfig, ClientsService } from "gen/clients";
import { DialogBody } from "components/DialogBody/DialogBody";
import { Form, Formik } from "formik";
import { SelectOption } from "@mui/base";
import DialogContent from "@mui/material/DialogContent";
import { SelectManyAutocompleteField } from "components/formikFields/SelectManyAutocompleteField/SelectManyAutocompleteField";
import DialogActions from "@mui/material/DialogActions";
import { LoadingButton } from "components/LoadingButton/LoadingButton";
import Button from "@mui/material/Button";
import { Stack, Typography } from "@mui/material";
import { SELECT_OPTION_COMPARATOR } from "utils/stringUtils";

interface Props {
  application: ApplicationSummary;
  clientConfig: ClientConfig;
  onSaveSuccess: () => void;
  onClose: () => void;
}

enum FieldName {
  CLIENT_CONTACTS = "clientContacts"
}

interface FormValues {
  [FieldName.CLIENT_CONTACTS]: SelectOption<string>[];
}

const UserAccessDialog: React.FC<Props> = ({ application, clientConfig, onSaveSuccess, onClose }) => {
  const {
    data: clientContacts,
    isLoading: areClientContactsLoading,
    error: clientContactsError
  } = useQuery(["listClientContacts", clientConfig.id], {
    queryFn: () => ClientsService.listClientContacts({ clientId: clientConfig.id })
  });

  const { mutateAsync } = useMutation(ClientsService.updateClientConfig);

  const clientContactOptions: SelectOption<string>[] = React.useMemo(() => {
    if (!clientContacts) {
      return [];
    }
    return clientContacts.items
      .map(c => ({
        label: `${c.firstName} ${c.lastName} (${c.email})`,
        value: `${c.id}`,
        disabled: !c.xtrfAccountStatus
      }))
      .sort(SELECT_OPTION_COMPARATOR);
  }, [clientContacts]);

  const onSubmit = React.useCallback(
    async (value: FormValues) => {
      await mutateAsync({
        id: clientConfig.id,
        requestBody: {
          ...clientConfig,
          appAccessAllowedListByAppId: {
            ...(clientConfig.appAccessAllowedListByAppId || {}),
            [application.id]: value[FieldName.CLIENT_CONTACTS].map(
              c => clientContacts!.items.find(cc => `${cc.id}` === c.value)!
            )
          }
        }
      });
      onSaveSuccess();
    },
    [application, clientConfig, clientContacts, mutateAsync, onSaveSuccess]
  );

  return (
    <Dialog open={true} fullWidth={true} maxWidth="md" onClose={onClose}>
      <DialogTitle>Edit User Access</DialogTitle>
      <DialogBody isLoading={areClientContactsLoading} error={clientContactsError}>
        <Formik
          initialValues={{
            [FieldName.CLIENT_CONTACTS]:
              clientConfig.appAccessAllowedListByAppId?.[application.id]?.map(c => ({
                label: `${c.firstName} ${c.lastName} (${c.email})`,
                value: `${c.id}`
              })) || []
          }}
          onSubmit={onSubmit}
        >
          {({ isSubmitting }) => (
            <Form noValidate={true} autoComplete="off" autoCorrect="off">
              <DialogContent>
                <Stack spacing={2}>
                  <Typography>
                    Select users that you would like to have access to <strong>{application.name}</strong> application:
                  </Typography>
                  <SelectManyAutocompleteField
                    name={FieldName.CLIENT_CONTACTS}
                    label="Client Contacts"
                    options={clientContactOptions}
                    variant="outlined"
                    matchFrom="any"
                  />
                </Stack>
              </DialogContent>
              <DialogActions>
                <LoadingButton isLoading={isSubmitting}>Save</LoadingButton>
                <Button onClick={onClose}>Cancel</Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </DialogBody>
    </Dialog>
  );
};

export { UserAccessDialog };
